import type { Environment } from "../../../client/stores";
import { Site } from "../decoders/Sites.types";

import {
    SitesDestinationType,
    SitesRedirect,
    SitesSearchOverride,
    Sites_Lookup_By,
    SitesRedirectQueryParamsHandling,
} from "../generated/webapi.g";
import { getSlugByBrandTag } from "../services/Sites";
import { getLiveStationUrl } from "./common";
import { logger } from "./logging";

const log = logger.getChild("redirects");

interface RedirectDestinationMap {
    [destinationType: string]: (
        redirect: SitesRedirect | SitesSearchOverride,
        path: string,
        source: RegExp | string,
    ) => Promise<string | null>;
}

export function isRedirect(redirect: SitesRedirect | SitesSearchOverride): redirect is SitesRedirect {
    return redirect.hasOwnProperty("regex");
}

export const redirectDestinationUrl = (
    redirect: SitesRedirect | SitesSearchOverride,
    path: string,
    source: RegExp | string,
) => {
    if (isRedirect(redirect) && redirect.regex) {
        return redirect.destination.value ? path.replace(source, redirect.destination.value) : null;
    }
    return redirect.destination.value || null;
};

export const redirectDestination: RedirectDestinationMap = {
    [SitesDestinationType.Url]: async (redirect, path, source) => redirectDestinationUrl(redirect, path, source),
    [SitesDestinationType.Brand]: async redirect => {
        if (!redirect.destination.value) {
            return null;
        }
        const { value: brand } = redirect.destination;
        const micrositeSlug = await getSlugByBrandTag({
            type: Sites_Lookup_By.Brand,
            value: brand,
        });
        return micrositeSlug ? `/featured/${micrositeSlug}/` : null;
    },
};

export function normalizePath(path: string) {
    return (path.endsWith("/") ? path : `${path}/`).toLowerCase();
}

export function pathMatches(left: string, right: string) {
    const normalizedPath = normalizePath(right);
    return normalizePath(left) === normalizedPath ? normalizedPath : undefined;
}

export function regexPathMatches(source: string, current: string) {
    const re = new RegExp(source);
    return normalizePath(current).match(re) ? re : undefined;
}

interface RedirectMatch {
    redirect: SitesRedirect | SitesSearchOverride;
    match: RegExp | string;
}

export const getRedirect = (site: Site, env: Environment, path: string): RedirectMatch | undefined => {
    const { config } = site;
    const liveUrl = getLiveStationUrl(site, env);
    const listenLiveRedirect: SitesRedirect | null = liveUrl
        ? {
              source: "/listen/",
              destination: {
                  value: liveUrl,
                  type: SitesDestinationType.Url,
              },
              permanent: true,
              id: "",
              disabled: false,
              regex: false,
              priority: 0,
              queryParamsHandling: SitesRedirectQueryParamsHandling.Original,
              matchQueryString: false,
          }
        : null;
    return (
        findRedirect(path, sortRedirects([...config.redirects, listenLiveRedirect])) ||
        findSearchOverride(path, config.searchOverrides)
    );
};

const sortRedirects = (redirects: Array<SitesRedirect | null>): SitesRedirect[] => {
    // Sort redirects so exact matches have precedence over regex and regex matches are sorted by priority
    const nonRegexRedirects: SitesRedirect[] = [];
    const regexRedirects: SitesRedirect[] = [];

    redirects.map(redirect => {
        if (!redirect) {
            return;
        }

        if (redirect.regex) {
            regexRedirects.push(redirect);
        } else {
            nonRegexRedirects.push(redirect);
        }
    });

    return [...nonRegexRedirects, ...regexRedirects.sort((a, b) => a.priority - b.priority)];
};

export const findRedirect = (path: string, redirects: SitesRedirect[]): RedirectMatch | undefined => {
    const now = Date.now();
    for (const redirect of redirects) {
        if (
            !redirect.source ||
            redirect.disabled ||
            (redirect.schedule && (redirect.schedule.begin > now || redirect.schedule.end < now))
        ) {
            continue;
        }

        const match = redirect.regex ? regexPathMatches(redirect.source, path) : pathMatches(redirect.source, path);
        if (match) {
            return { redirect, match };
        }
    }
};

export const findSearchOverride = (path: string, overrides: SitesSearchOverride[]): RedirectMatch | undefined => {
    const now = Date.now();
    for (const override of overrides) {
        if (
            !override.disabled &&
            (!override.schedule || (override.schedule.begin <= now && override.schedule.end > now))
        ) {
            for (const keyword of override.keywords) {
                const match = pathMatches(`/search/${encodeURIComponent(keyword.toLowerCase().trim())}`, path);
                if (match) {
                    log.debug("Search override match found:", override);
                    return {
                        redirect: override,
                        match,
                    };
                }
            }
        }
    }
};
